// Loja e saldos: vendas no balcão, produtos, pacotes, vale-presente e fidelidade.
// O dinheiro é calculado de novo no servidor; aqui só a prévia e a escolha.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, useSyncExternalStore } from 'react';
import { api, newKey } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { can, useAgenda, useClients, useMe, useServices, type ClientRow } from '../lib/data.ts';
import { first, money, moneyShort, nowLocal, parseMoney } from '../lib/format.ts';
import { useSettingsEdit } from '../lib/settings.ts';
import { STORE_KEYS, useGiftCards, useProducts, useSoldPackages, type GiftCard } from '../lib/store.ts';
import { Avatar } from '../ui/bits.tsx';
import { CfgNum, CfgText, CfgToggle, Saved } from '../ui/cfg.tsx';
import { BackHeader } from '../ui/Header.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Empty } from '../ui/Empty.tsx';
import { useSalePay } from '../ui/pay.tsx';
import { Sheet } from '../ui/Sheet.tsx';

const br = (d: string) => `${d.slice(8)}/${d.slice(5, 7)}/${d.slice(2, 4)}`;
function useInvalidateStore() {
  const qc = useQueryClient();
  return () => Promise.all(STORE_KEYS.map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

// ---------- sacola do balcão (fica entre a prateleira e o pagamento) ----------
type Cart = Record<string, number>;
let cart: Cart = {};
const subs = new Set<() => void>();
const setCart = (c: Cart) => { cart = c; subs.forEach((f) => f()); };
const useCart = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => cart);
const addTo = (id: string, n: number) => { const q = Math.max(0, (cart[id] ?? 0) + n); const c = { ...cart }; if (q) c[id] = q; else delete c[id]; setCart(c); };

export function Vendas() {
  const nav = useNav();
  const c = useCart();
  const list = (useProducts().data ?? []).filter((p) => p.active);
  const n = Object.values(c).reduce((t, q) => t + q, 0);
  const total = list.reduce((t, p) => t + (c[p.id] ?? 0) * p.priceCents, 0);
  return (
    <div className="view-enter"><BackHeader title="Vendas no balcão" />
      {!list.length ? <Empty icon="kit" title="Nenhum produto na prateleira" action={<button className="cta sm" onClick={() => nav.go('produtos')}>Cadastrar produtos</button>}>Cadastre o que o salão vende, com preço e estoque. Depois é só tocar no produto para vender.</Empty> : (
        <div className="shelf">{list.map((p) => {
          const q = c[p.id] ?? 0;
          return (
            <div key={p.id} className={'shelf-item' + (q ? ' in' : '')}><span className="p-art" aria-hidden="true"><Icon name="drop" /></span>
              <span className="shelf-name"><strong>{p.name}</strong><small>{p.description}{p.tracksStock ? `${p.description ? ' · ' : ''}${p.stock} em estoque` : ''}</small><em>{money(p.priceCents)}</em></span>
              {q ? <span className="shelf-step"><button aria-label={`Tirar um ${p.name}`} onClick={() => addTo(p.id, -1)}><Icon name="minus" /></button><b aria-live="polite">{q}</b><button aria-label={`Mais um ${p.name}`} onClick={() => addTo(p.id, 1)}><Icon name="plus" /></button></span>
                : <button className="add-btn" aria-label={`Adicionar ${p.name}`} onClick={() => addTo(p.id, 1)}><Icon name="plus" /></button>}
            </div>
          );
        })}</div>
      )}
      <div className="note" style={{ margin: '10px var(--gutter) 0' }}><Icon name="info" /><span>Venda avulsa no balcão não gera cota para as parceiras. Produto vendido na comanda gera a comissão de produto da parceira.</span></div>
      {n > 0 && <div className="pb-bar cart-bar"><div className="pb-bar-sum"><small>{n} {n > 1 ? 'itens' : 'item'}</small><strong>{money(total)}</strong></div><button className="pb-go" onClick={() => nav.open({ kind: 'carrinho' })}>Receber</button></div>}
      <div style={{ height: 90 }} />
    </div>
  );
}

// escolher a cliente: as de hoje primeiro, depois a busca
function ClientPick({ value, onChange, optional }: { value: string | null; onChange: (id: string | null, name?: string) => void; optional?: boolean }) {
  const [q, setQ] = useState('');
  const today = useAgenda(nowLocal().date).data ?? [];
  const found = useClients(q, 'all').data ?? [];
  const seen = new Set<string>();
  const todays = today.filter((a) => { if (a.status === 'cancelled' || seen.has(a.clientId)) return false; seen.add(a.clientId); return true; }).map((a) => ({ id: a.clientId, name: a.client }));
  const list: Array<{ id: string; name: string }> = q.length >= 2 ? (found as ClientRow[]).slice(0, 12) : todays.slice(0, 12);
  return (
    <>
      <div className="search-in"><Icon name="search" /><input type="search" placeholder="Buscar cliente" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar cliente" /></div>
      <div className="kit-clients cart-who">
        {optional && <button className={!value ? 'on' : ''} aria-pressed={!value} onClick={() => onChange(null)}><span className="av none"><Icon name="users" /></span>Sem cliente</button>}
        {list.map((c) => <button key={c.id} className={value === c.id ? 'on' : ''} aria-pressed={value === c.id} onClick={() => onChange(c.id, c.name)}><Avatar id={c.id} name={c.name} />{first(c.name)}</button>)}
      </div>
      {q.length < 2 && !todays.length && <p className="m-hint">Digite o nome para achar a cliente.</p>}
    </>
  );
}

export function CarrinhoSheet() {
  const nav = useNav();
  const toast = useToast();
  const refresh = useInvalidateStore();
  const c = useCart();
  const products = useProducts().data ?? [];
  const pay = useSalePay();
  const key = useRef(newKey()).current;
  const [clientId, setClientId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ total: number; n: number } | null>(null);
  const lines = products.filter((p) => c[p.id]).map((p) => ({ p, q: c[p.id]! }));
  const total = lines.reduce((t, l) => t + l.q * l.p.priceCents, 0);
  if (done) return <Sheet title="Venda no balcão" actions={<button className="cta" onClick={() => { nav.close(); }}>Pronto</button>}>
    <div className="ok-hd"><span className="ok-ic" aria-hidden="true"><Icon name="check" /></span><h3>Vendido</h3><p>{done.n} {done.n > 1 ? 'itens' : 'item'} · {money(done.total)}</p></div></Sheet>;
  async function receive() {
    if (pay.missing || !lines.length) return;
    setBusy(true);
    try {
      await api.post('/sales', { idempotencyKey: key, clientId, products: lines.map((l) => ({ productId: l.p.id, quantity: l.q })), payments: pay.payments(total) });
      setDone({ total, n: lines.reduce((t, l) => t + l.q, 0) }); setCart({}); await refresh();
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }
  return (
    <Sheet title="Receber venda" actions={<button className="cta" disabled={!!pay.missing || busy || !lines.length} onClick={receive}>{busy ? 'Recebendo…' : pay.missing || `Receber ${money(total)}`}</button>}>
      {lines.map((l) => <div key={l.p.id} className="cline"><button className="dec-btn" aria-label={`Tirar um ${l.p.name}`} onClick={() => addTo(l.p.id, -1)}><Icon name="minus" /></button><span className="name"><b>{l.p.name}</b><small>{l.q} × {money(l.p.priceCents)}</small></span><b>{money(l.q * l.p.priceCents)}</b></div>)}
      <div className="total-card2"><small>Total da venda</small><strong>{money(total)}</strong></div>
      <div className="section-title">Para quem? <small>opcional</small></div>
      <ClientPick value={clientId} onChange={setClientId} optional />
      <div className="section-title">Pagamento</div>
      {pay.view}
    </Sheet>
  );
}

// ---------- produtos (cadastro e estoque) ----------
export function Produtos() {
  const nav = useNav();
  const q = useProducts();
  const list = q.data ?? [];
  return (
    <div className="view-enter cp2"><BackHeader title="Produtos" label="Configurações" />
      <div className="cp2-body">
        <div className="gl">
          {list.map((p) => (
            <button key={p.id} className="gl-row gl-btn" onClick={() => nav.open({ kind: 'produto', id: p.id })}>
              <b>{p.name}<small>{money(p.priceCents)}{p.tracksStock ? ` · ${p.stock} em estoque` : ''}{p.tracksStock && p.stock <= p.minStock ? ' · repor' : ''}{p.active ? '' : ' · fora da venda'}{p.soldThisMonth ? ` · ${p.soldThisMonth} vendidos no mês` : ''}</small></b><Icon name="chevR" /></button>
          ))}
          {q.data === undefined ? (q.isError ? <Failed compact error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={4} />) : !list.length && <p className="m-hint rg-hint">Nenhum produto ainda.</p>}
          <button className="gl-row gl-btn ac-invite" onClick={() => nav.open({ kind: 'produto' })}><b>+ Adicionar produto</b></button>
        </div>
        <p className="m-hint">O estoque é a soma das entradas, vendas e acertos. Acerto pede o motivo e fica no histórico.</p>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

export function ProdutoSheet({ id }: { id?: string }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useInvalidateStore();
  const me = useMe().data!;
  const p = (useProducts().data ?? []).find((x) => x.id === id);
  const [f, setF] = useState(() => ({ name: p?.name ?? '', description: p?.description ?? '', price: p ? (p.priceCents / 100).toFixed(2).replace('.', ',') : '', cost: p && p.costCents ? (p.costCents / 100).toFixed(2).replace('.', ',') : '', tracksStock: p?.tracksStock ?? true, minStock: p?.minStock ?? 0, active: p?.active ?? true, initial: 0 }));
  const [mov, setMov] = useState<{ delta: number; reason: 'purchase' | 'adjust'; note: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (id && !p) return <Sheet title="Produto"><Skeleton kind="sheet" rows={3} /></Sheet>;
  const price = parseMoney(f.price), cost = f.cost ? parseMoney(f.cost) : 0;
  const miss = f.name.trim().length < 2 ? 'Falta o nome' : !(price >= 0) || !f.price ? 'Falta o preço' : '';
  async function save() {
    if (miss) return;
    setBusy(true);
    try {
      const body = { name: f.name.trim(), description: f.description.trim(), priceCents: price, costCents: cost || 0, tracksStock: f.tracksStock, minStock: f.minStock, active: f.active };
      if (p) await api.put(`/products/${p.id}`, { ...body, version: p.version });
      else await api.post('/products', { ...body, initialStock: f.tracksStock ? f.initial : 0 });
      await refresh(); toast(p ? 'Produto salvo.' : `${body.name} na prateleira.`); nav.close();
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }
  async function moveStock() {
    if (!p || !mov || !mov.delta) return;
    try { await api.post(`/products/${p.id}/stock`, mov); await refresh(); setMov(null); toast('Estoque atualizado.'); } catch (e) { toast(msg(e), 'warn'); }
  }
  return (
    <Sheet title={p ? p.name : 'Novo produto'} sub={p?.tracksStock ? `${p.stock} em estoque` : undefined} actions={<><button className="cta" disabled={!!miss || busy} onClick={save}>{busy ? 'Salvando…' : miss || 'Salvar'}</button><button className="link-back" onClick={nav.close}>Cancelar</button></>}>
      <div className="field"><label htmlFor="pdName">Nome</label><input id="pdName" className="text-in" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Óleo de cutícula" /></div>
      <div className="field"><label htmlFor="pdDesc">Detalhe <span>opcional</span></label><input id="pdDesc" className="text-in" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Ex.: frasco 30 ml" /></div>
      <div className="field"><label htmlFor="pdPrice">Preço de venda</label><div className="money-in"><span>R$</span><input id="pdPrice" inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="0,00" /></div></div>
      {can(me, 'valoresSalao') && <div className="field"><label htmlFor="pdCost">Quanto custou <span>opcional</span></label><div className="money-in"><span>R$</span><input id="pdCost" inputMode="decimal" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} placeholder="0,00" /></div></div>}
      <div className="gl" style={{ marginTop: 12 }}>
        <CfgToggle label="Controlar estoque" value={f.tracksStock} onChange={(v) => setF({ ...f, tracksStock: v })} />
        {f.tracksStock && !p && <CfgNum label="Estoque inicial" value={f.initial} lim={[0, 9999, 1]} onChange={(v) => setF({ ...f, initial: v })} />}
        {f.tracksStock && <CfgNum label="Avisar quando chegar a" value={f.minStock} lim={[0, 999, 1]} onChange={(v) => setF({ ...f, minStock: v })} />}
        {p && <CfgToggle label="À venda" value={f.active} onChange={(v) => setF({ ...f, active: v })} />}
      </div>
      {p && p.tracksStock && can(me, 'valoresSalao') && (
        <>
          <p className="gl-title">Estoque</p>
          {!mov ? <div className="row-acts"><button className="cta ghost sm" onClick={() => setMov({ delta: 1, reason: 'purchase', note: '' })}>Chegou mercadoria</button><button className="cta ghost sm" onClick={() => setMov({ delta: -1, reason: 'adjust', note: '' })}>Acertar contagem</button></div> : (
            <div className="gl">
              <CfgNum label={mov.reason === 'purchase' ? 'Quantos chegaram' : 'Mais ou menos'} value={mov.delta} lim={mov.reason === 'purchase' ? [1, 9999, 1] : [-999, 999, 1]} onChange={(v) => setMov({ ...mov, delta: v })} />
              {mov.reason === 'adjust' && <CfgText label="Motivo" value={mov.note} placeholder="Ex.: contagem, quebrou" onChange={(v) => setMov({ ...mov, note: v })} />}
              <div className="gl-row"><span /><span className="row-acts"><button className="gl-act" onClick={() => setMov(null)}>Cancelar</button><button className="gl-act" disabled={!mov.delta || (mov.reason === 'adjust' && mov.note.trim().length < 3)} onClick={moveStock}>Lançar</button></span></div>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}

// ---------- pacotes ----------
export function Pacotes() {
  const nav = useNav();
  const q = useSoldPackages();
  const list = q.data ?? [];
  const open = list.filter((p) => p.remaining > 0 && !p.expired);
  const owed = open.reduce((t, p) => t + p.remaining * p.sessionValueCents, 0);
  return (
    <div className="view-enter"><BackHeader title="Pacotes" />
      {list.length > 0 && <><div className="kit-sum"><div><span>Sessões a fazer</span><strong>{open.reduce((t, p) => t + p.remaining, 0)}</strong><small>em {open.length} {open.length === 1 ? 'pacote ativo' : 'pacotes ativos'}</small></div>
        <div><span>Já pago, a realizar</span><strong>{moneyShort(owed)}</strong><small>o salão deve em serviço</small></div></div>
      <button className="cta kit-cta" onClick={() => nav.open({ kind: 'pacote' })}><Icon name="plus" />Vender pacote</button></>}
      <div className="pk-list">{list.map((p) => {
        const used = p.sessions - p.remaining, done = p.remaining <= 0;
        return (
          <div key={p.id} className={'pk-card' + (done || p.expired ? ' done' : '')}>
            <div className="pk-top"><span><strong>{p.client}</strong><small>{p.service} · {p.expired ? `venceu em ${br(p.expiresOn)}` : `válido até ${br(p.expiresOn)}`}</small></span>
              <em>{done ? 'Concluído' : p.expired ? 'Vencido' : p.remaining === 1 ? 'Última sessão' : `Restam ${p.remaining}`}</em></div>
            <div className="pk-dots">{Array.from({ length: p.sessions }, (_, i) => <i key={i} className={i < used ? 'on' : ''}>{i + 1}</i>)}</div>
            <div className="pk-foot"><span>sessão vale {money(p.sessionValueCents)}</span>{!done && <button onClick={() => nav.go('cliente', { id: p.clientId! })}>Ver cliente<Icon name="chev" /></button>}</div>
          </div>
        );
      })}</div>
      {!list.length && !q.isLoading && <Empty icon="spa" title="Nenhum pacote vendido ainda" action={<button className="cta sm" onClick={() => nav.open({ kind: 'pacote' })}>Vender pacote</button>}>Pacote é um conjunto de sessões pagas antes, como 4 manicures. Cada atendimento usa uma sessão.</Empty>}
      <div className="note" style={{ margin: '12px var(--gutter) 0' }}><Icon name="info" /><span>A sessão é baixada na comanda do atendimento, e a cota da parceira entra nessa hora, não na venda.</span></div>
      <div style={{ height: 16 }} />
    </div>
  );
}

export function PacoteSheet({ clientId: preset }: { clientId?: string }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useInvalidateStore();
  const me = useMe().data!;
  const services = (useServices().data ?? []).filter((s) => s.active);
  const rules = me.salon.settings.packageRules?.length ? me.salon.settings.packageRules : [{ sessions: 4, offBps: 1000, days: 60 }];
  const pay = useSalePay();
  const key = useRef(newKey()).current;
  const [clientId, setClientId] = useState<string | null>(preset ?? null);
  const [clientName, setClientName] = useState('');
  const [svcId, setSvcId] = useState<string | null>(null);
  const [n, setN] = useState(rules[0]!.sessions);
  const [busy, setBusy] = useState(false);
  const svc = services.find((s) => s.id === svcId);
  const rule = rules.find((r) => r.sessions === n) ?? rules[0]!;
  const quote = (s: number, r: typeof rule) => { const each = Math.round((s * (10_000 - r.offBps)) / 10_000 / 100) * 100; return { each, total: each * r.sessions }; };
  const q = svc ? quote(svc.priceCents, rule) : null;
  const miss = !clientId ? 'Escolha a cliente' : !svc ? 'Escolha o serviço' : pay.missing;
  async function sell() {
    if (miss || !q || !svc) return;
    setBusy(true);
    try {
      await api.post('/sales', { idempotencyKey: key, clientId, package: { serviceId: svc.id, sessions: rule.sessions, priceCents: q.total, validDays: rule.days }, payments: pay.payments(q.total) });
      await refresh(); toast(`Pacote de ${rule.sessions} sessões vendido${clientName ? ` para ${first(clientName)}` : ''}. ${money(q.total)} no caixa.`); nav.close();
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }
  return (
    <Sheet title="Vender pacote" actions={<button className="cta" disabled={!!miss || busy} onClick={sell}>{busy ? 'Vendendo…' : miss || `Vender ${rule.sessions}× ${svc!.name} · ${money(q!.total)}`}</button>}>
      <div className="section-title" style={{ marginTop: 0 }}>Cliente</div>
      {preset ? <p className="m-hint">Para a cliente desta ficha.</p> : <ClientPick value={clientId} onChange={(id, name) => { setClientId(id); setClientName(name ?? ''); }} />}
      <div className="section-title">Serviço</div>
      <div className="chipset kit-chips">{services.map((s) => <button key={s.id} className={svcId === s.id ? 'on' : ''} aria-pressed={svcId === s.id} onClick={() => setSvcId(s.id)}>{s.name}</button>)}</div>
      {svc && <>
        <div className="section-title">Tamanho</div>
        <div className="kit-sizes">{rules.map((r) => (
          <button key={r.sessions} className={n === r.sessions ? 'on' : ''} aria-pressed={n === r.sessions} onClick={() => setN(r.sessions)}><b>{r.sessions}</b><span>sessões</span><small>{r.offBps / 100}% off</small><em>{moneyShort(quote(svc.priceCents, r).total)}</em></button>
        ))}</div>
        <div className="kit-quote"><div><span>Cada sessão</span><b>{money(q!.each)}</b><s>{money(svc.priceCents)}</s></div><div><span>Economia</span><b>{money((svc.priceCents - q!.each) * rule.sessions)}</b></div><div><span>Validade</span><b>{rule.days} dias</b></div></div>
      </>}
      <div className="section-title">Pagamento</div>
      {pay.view}
    </Sheet>
  );
}

// ---------- vale-presente ----------
function GiftArt({ g, big }: { g: Pick<GiftCard, 'code' | 'amountCents' | 'balance' | 'recipientName' | 'buyerName'>; big?: boolean }) {
  const used = g.balance <= 0;
  return (
    <div className={'gift' + (used ? ' used' : '') + (big ? ' big' : '')}>
      <div className="gift-top"><div className="wordmark-mark gift-wm" role="img" aria-label="Sublime Nail Design" /><span>Vale-presente</span></div>
      <strong className="gift-amt">{money(used ? g.amountCents : g.balance)}</strong>
      <div className="gift-who"><span>Para <b>{g.recipientName || '—'}</b></span><span>De <b>{g.buyerName || '—'}</b></span></div>
      <div className="gift-foot"><code>{g.code}</code><small>{used ? 'usado' : g.balance < g.amountCents ? `saldo de ${money(g.amountCents)}` : 'válido por 12 meses'}</small></div>
    </div>
  );
}

export function Vales() {
  const nav = useNav();
  const q = useGiftCards();
  const list = q.data ?? [];
  const open = list.reduce((t, g) => t + Math.max(0, g.balance), 0);
  return (
    <div className="view-enter"><BackHeader title="Vale-presente" />
      {list.length > 0 && <><div className="kit-sum one"><div><span>Saldo em aberto</span><strong>{moneyShort(open)}</strong><small>já recebido; vira serviço quando a presenteada vier</small></div></div>
      <button className="cta kit-cta" onClick={() => nav.open({ kind: 'vale' })}><Icon name="gift" />Emitir vale-presente</button></>}
      <div className="gift-list">{[...list].sort((a, b) => Number(a.balance <= 0) - Number(b.balance <= 0)).map((g) => (
        <div key={g.id} className="gift-row"><GiftArt g={g} /><p>{g.balance <= 0 ? 'Usado por completo' : `Emitido em ${br(g.createdAt.slice(0, 10))} · vale até ${br(g.expiresOn)}`}{g.message ? ` · “${g.message}”` : ''}</p></div>
      ))}</div>
      {!list.length && !q.isLoading && <Empty icon="gift" title="Nenhum vale emitido ainda" action={<button className="cta sm" onClick={() => nav.open({ kind: 'vale' })}>Emitir vale-presente</button>}>O vale-presente vira crédito para quem ganhou. Ele é usado na cobrança, inteiro ou em partes.</Empty>}
      <div className="note" style={{ margin: '12px var(--gutter) 0' }}><Icon name="info" /><span>O vale é usado como forma de pagamento na cobrança, inteiro ou em partes, pelo código.</span></div>
      <div style={{ height: 16 }} />
    </div>
  );
}

export function ValeSheet() {
  const nav = useNav();
  const toast = useToast();
  const refresh = useInvalidateStore();
  const pay = useSalePay();
  const key = useRef(newKey()).current;
  const [amount, setAmount] = useState<number | 'outro'>(10_000);
  const [custom, setCustom] = useState('');
  const [to, setTo] = useState('');
  const [from, setFrom] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<GiftCard | null>(null);
  const v = amount === 'outro' ? parseMoney(custom) : amount;
  const miss = !(v >= 1000) ? 'Valor mínimo de R$ 10' : to.trim().length < 2 ? 'Para quem é?' : from.trim().length < 2 ? 'Quem está dando?' : pay.missing;
  if (issued) {
    const text = `${issued.recipientName}, você ganhou um vale-presente de ${money(issued.amountCents)} na Sublime Nail Design${issued.message ? `: "${issued.message}"` : ''}. Código ${issued.code}, válido até ${br(issued.expiresOn)}. É só mostrar o código no dia.`;
    return (
      <Sheet title="Vale emitido" actions={<><a className="cta cta-ic" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener"><Icon name="message" />Enviar pelo WhatsApp</a><button className="cta ghost" onClick={nav.close}>Pronto</button></>}>
        <div className="gift-done"><GiftArt g={{ ...issued, balance: issued.amountCents }} big /><p>Pago e somado ao caixa de hoje. O código funciona na cobrança como forma de pagamento.</p></div>
      </Sheet>
    );
  }
  async function issue() {
    if (miss) return;
    setBusy(true);
    try {
      const r = await api.post<{ giftCard: GiftCard }>('/sales', { idempotencyKey: key, giftCard: { amountCents: v, recipientName: to.trim(), buyerName: from.trim(), message: note.trim() }, payments: pay.payments(v) });
      setIssued({ ...r.giftCard, balance: r.giftCard.amountCents }); await refresh();
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }
  return (
    <Sheet title="Emitir vale-presente" actions={<button className="cta" disabled={!!miss || busy} onClick={issue}>{busy ? 'Emitindo…' : miss || `Receber ${money(v)} e emitir`}</button>}>
      <GiftArt g={{ code: 'SUB-······', amountCents: v || 0, balance: v || 0, recipientName: to, buyerName: from }} />
      <div className="section-title">Valor</div>
      <div className="chipset kit-chips">{[5000, 10_000, 15_000, 20_000].map((x) => <button key={x} className={amount === x ? 'on' : ''} aria-pressed={amount === x} onClick={() => setAmount(x)}>{moneyShort(x)}</button>)}
        <button className={amount === 'outro' ? 'on' : ''} aria-pressed={amount === 'outro'} onClick={() => setAmount('outro')}>Outro</button></div>
      {amount === 'outro' && <div className="field"><div className="money-in"><span>R$</span><input aria-label="Outro valor" inputMode="decimal" placeholder="0,00" value={custom} onChange={(e) => setCustom(e.target.value)} autoFocus /></div></div>}
      <div className="field"><label htmlFor="giftTo">Para quem</label><input id="giftTo" className="text-in" placeholder="Nome de quem vai ganhar" value={to} onChange={(e) => setTo(e.target.value)} /></div>
      <div className="field"><label htmlFor="giftFrom">Quem está dando</label><input id="giftFrom" className="text-in" placeholder="Nome de quem paga" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
      <div className="field"><label htmlFor="giftMsg">Mensagem no cartão <span>opcional</span></label><input id="giftMsg" className="text-in" placeholder="Feliz aniversário!" value={note} maxLength={140} onChange={(e) => setNote(e.target.value)} /></div>
      <div className="section-title">Pagamento</div>
      {pay.view}
    </Sheet>
  );
}

// ---------- fidelidade ----------
function Stamps({ v, target }: { v: number; target: number }) {
  return <span className="stamps" aria-label={`${Math.min(v, target)} de ${target} selos`}>{Array.from({ length: target }, (_, i) => <i key={i} className={(i < v ? 'on' : '') + (i === target - 1 ? ' prize' : '')}>{i === target - 1 ? <Icon name="gift" /> : null}</i>)}</span>;
}

export function Fidelidade() {
  const nav = useNav();
  const me = useMe().data!;
  const { settings, save, savedAt } = useSettingsEdit();
  const l = settings?.loyalty;
  // a lista muda quando a regra muda (meta e validade dos selos)
  const items = useQuery({ queryKey: ['loyalty', l?.visits, l?.months], queryFn: () => api.get<{ items: Array<{ id: string; name: string; stamps: number }> }>('/loyalty').then((r) => r.items), enabled: !!l?.on }).data;
  if (!settings || !l) return <div className="view-enter"><BackHeader title="Fidelidade" /></div>;
  const set = (v: Partial<typeof l>) => save((s) => ({ ...s, loyalty: { ...s.loyalty, ...v } }));
  const list = items ?? [];
  const ready = list.filter((x) => x.stamps >= l.visits), near = list.filter((x) => x.stamps < l.visits && l.visits - x.stamps <= 3), rest = list.filter((x) => l.visits - x.stamps > 3);
  const group = (title: string, arr: typeof list) => arr.length ? (
    <div className="hsec"><div className="hsec-label"><b>{title}</b><span className="hsec-n">{arr.length}</span></div><div className="loy-list">{arr.map((x) => {
      const left = l.visits - x.stamps;
      return <button key={x.id} className={'loy-row' + (left <= 0 ? ' full' : '')} onClick={() => nav.go('cliente', { id: x.id })}><span className="loy-who"><strong>{x.name}</strong><small>{left <= 0 ? 'Recompensa liberada · resgate na cobrança' : left === 1 ? 'Falta 1 selo' : `Faltam ${left} selos`}</small></span><Stamps v={x.stamps} target={l.visits} /></button>;
    })}</div></div>
  ) : null;
  return (
    <div className="view-enter"><BackHeader title="Fidelidade" />
      <div className="loy-hero"><i className="ill selo" aria-hidden="true" /><span className="pill-live">{l.on ? `A cada ${l.visits} atendimentos` : 'Desligada'}</span>
        <h2>{l.rewardLabel}</h2><p>Cada atendimento concluído vale 1 selo. O resgate é feito na cobrança, e os selos valem por {l.months} meses.</p>
        {l.on && <div className="loy-sum"><span><b>{ready.length}</b> com recompensa</span><span><b>{near.length}</b> perto de ganhar</span></div>}</div>
      {can(me, 'config') && <>
        <p className="gl-title fid-ind">Regras<Saved at={savedAt} /></p>
        <div className="gl fid-ind-gl">
          <CfgToggle label="Programa de fidelidade" value={l.on} onChange={(v) => set({ on: v })} />
          {l.on && <>
            <CfgNum label="Atendimentos para ganhar" value={l.visits} lim={[3, 30, 1]} onChange={(v) => set({ visits: v })} />
            <CfgText label="Recompensa" value={l.rewardLabel} onChange={(v) => set({ rewardLabel: v.trim() || l.rewardLabel })} />
            <CfgNum label="Vale até" kind="money" value={l.rewardCents} lim={[0, 100_000, 500]} onChange={(v) => set({ rewardCents: v })} hint="abatido do serviço no resgate" />
            <CfgNum label="Selos valem por" unit="meses" value={l.months} lim={[3, 24, 1]} onChange={(v) => set({ months: v })} />
          </>}
        </div>
      </>}
      {l.on && <>{group('Recompensa liberada', ready)}{group('Perto da recompensa', near)}{group('Em andamento', rest)}</>}
      {l.on && items && !items.length && <p className="m-hint rg-hint" style={{ margin: '12px var(--gutter)' }}>Os selos aparecem conforme os atendimentos são concluídos.</p>}
      <div style={{ height: 16 }} />
    </div>
  );
}
